import { useContext } from "react";
import { useEffect } from "react";
import { useState } from "react";
import Button from '../../components/@base/Button';
import HTMLIcon from '../../assets/icon-html.png';
import PDFIcon from '../../assets/icon-pdf.png';
import DOCXIcon from '../../assets/icon-docx.png';
import TextIcon from '../../assets/icon-text.png';
import Input from '../../components/@base/Input';

/**
 * Componente que exibe uma lista de DataSources.
 * 
 * @param {*} props 
 * @returns 
 */
export const DataSources = (props) => {
    const emptyDataSource = {name: "", file: "", type: "", fileName: ""};
    const [dataSources, setDataSources] = useState(props.dataSources);
    const [dataSource, setDataSource] = useState(emptyDataSource);
    const [isCreating, setIsCreating] = useState(false);
    const [types, setTypes] = useState(props.types);
    const [validation, setValidation] = useState({name: false, file: false, messages: []});
    const onCreate = props.onCreate;
    const onDelete = props.onDelete;

    useEffect(() => {
        setDataSources(props.dataSources);
        setTypes(props.types);
    }, [props.dataSources, props.types]);

    /**
     * Trata o evento que é lançado ao clicar no botão de criação de 
     * uma fonte de dados. Repassa o evento para o componente logo acima deste.
     * 
     */
    const handleCreate = () => {
        if (!validation.name) {
            validation.messages.push("Informe o nome da fonte de dados.");
            return;
        }

        if (!validation.file) {
            validation.messages.push("Informe o arquivo da fonte de dados.");
            return;
        }

        setIsCreating(false);
        setDataSource({ name: "", file: "", type: "", fileName: ""});
        onCreate?.({
            name: dataSource.name,
            type: dataSource.type,
            fileName: dataSource.fileName,
            file: dataSource.file
        });
    };

    /**
     * Trata o evento que é lançado ao clicar no botão para remover
     * uma fonte de dados.
     * 
     * @param dataSource DataSource para excluir.
     */
    const handleDelete = (dataSource) => {
        onDelete?.(dataSource);
    };

    /**
     * Trata o evento que é lançado quando se clica no botão para
     * criar um novo datasource. 
     * 
     * É exibida uma linha a mais na listagem, para que o usuário informe
     * o nome e o arquivo. O flag isCreating controla a exibição desta linha
     * a mais.
     */
    const handleRequestCreate = () => {
        setDataSource(emptyDataSource);
        setValidation({name: false, file: false, messages: []});
        setIsCreating(true);
    };

    /**
     * Trata o evento que é lançado quando se clica no botão para
     * cancelar a criação de um novo datasource.
     * 
     * O flag isCreating controla a exibição da linha a mais para
     * a adição.
     */
    const handleCancelRequestCreate = () => {
        setIsCreating(false);
    };

    /**
     * Trata o evento que é lançado quando o nome do datasource é modificado.
     * 
     * Verifica se o valor digitado é válido, definindo uma mensagem de erro
     * e setando a validação como falha.
     * 
     * @param {*} event 
     */
    const handleChangeName = (event) => {
        const value = event.target.value;

        if (!value) {
            setValidation({...validation, name: false});
        } else {
            setValidation({...validation, name: true});
        }

        setDataSource({...dataSource, name: event.target.value});
    };

    /**
     * Trata o evento que é lançado quando um novo arquivo é selecionado.
     * O binário do arquivo é transformado em Base64 para ser enviado à API.
     * 
     * @param {*} event 
     */
    const handleChangeDataSourceFile = (file) => {
        const ext = file.name.split(".").pop();
        const type = types.find((type) => {
            return type.allowedFormats.split(",").indexOf(ext) > -1;
        });

        if (!type) {
            setValidation({...validation, file: false, messages:[`O formato do arquivo informado é inválido.`]});
        } else {
            if (file.size > type.streamMaxSize) {
                setValidation({...validation, file: false, messages:[`O arquivo informado é maior que o tamanho máximo permitido: ${type.streamMaxSize} bytes`]});
            } else {
                setValidation({...validation, file: true, messages:[]});

                const reader = new FileReader();

                reader.onload = (event) => {
        
                    // Precisamos remover a parte inicial da string, pois contém dados de URL (application/pdf, por exemplo).
                    // Essa informação no início não faz parte do binário do Base64.
                    const data = event.target.result.replace('data:', '').replace(/^.+,/, '')
                    
                    setDataSource({...dataSource, file: data, type: type.id, fileName: file.name});
                };
        
                reader.readAsDataURL(file);
            }
        }
    };

    /**
     * Obtém o ícone que representa o tipo do DataSource.
     * 
     * @param {string} typeName Nome do tipo do datasource.
     * @returns Ícone que representa o tipo do datasource.
     */
    const getDataSourceTypeIcon = (typeName) => {
        switch(typeName) {
            case "html": return HTMLIcon;
            case "docx": return DOCXIcon;
            case "pdf": return PDFIcon;
            case "text": return TextIcon;
        }
    };

    return (
        <div>
            <span style={{color: 'red', fontSize: '10pt'}}>{validation.messages}</span>
            <table>
                <thead>
                    <tr>
                        <th style={{width: '50%'}}>Fonte de Dados</th>
                        <th style={{width: '40%'}}></th>
                        <th style={{width: '10%', textAlign: 'right'}}>
                            <Button
                                style={{marginLeft: '50%'}}
                                circle
                                small
                                onClick={handleRequestCreate}>
                                <small>
                                    <i className="fa fa-plus"></i>
                                </small>
                            </Button>     
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {isCreating ? 
                    <tr>
                        <td>
                            <Input
                                invalid={!validation.name}
                                width={'100%'}
                                placeholder="Informe o nome da fonte de dados"
                                value={dataSource.name}
                                onChange={(event) => { handleChangeName(event) }}
                            />                            
                        </td>
                        <td>
                        <Input
                                invalid={!validation.file}
                                file
                                width={'100%'}
                                placeholder="Informe o arquivo da fonte de dados"
                                onChange={(event) => handleChangeDataSourceFile(event.target.files[0]) }
                            />                               
                        </td>
                        <td>
                            <div className="row">
                                <Button
                                    disabled={!validation.name || !validation.file}
                                    circle
                                    small
                                    onClick={handleCreate}>
                                    <small>
                                        <i className="fa fa-save"></i>
                                    </small>
                                </Button>                            
                                <Button
                                    circle
                                    small
                                    onClick={handleCancelRequestCreate}>
                                    <small>
                                        <i className="fa fa-trash"></i>
                                    </small>
                                </Button>
                            </div>
                        </td>
                    </tr> : null}
                    {dataSources.map((dataSource, index) => (
                        <tr key={index}>
                            <td><img width={36} height={36} src={getDataSourceTypeIcon(dataSource.typeName)} />
                                {dataSource.name} <small>({dataSource.origin})</small>
                            </td>
                            <td>
                                <div className="row">
                                    <Button small>
                                        <span style={{fontSize: '8pt'}}>{dataSource.length} caracteres</span>
                                    </Button>&nbsp;
                                    <Button small>
                                        <span style={{fontSize: '8pt'}}>{dataSource.wordsCount} palavras</span>
                                    </Button>&nbsp;
                                    <Button small>
                                        <span style={{fontSize: '8pt'}}>{dataSource.tokensCount} tokens</span>
                                    </Button>                                      

                                </div>
                            </td>
                            <td>
                                <Button 
                                    small
                                    onClick={() => handleDelete(dataSource)}>
                                    <i className="fa fa-trash" aria-hidden={true}></i>
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>       
        </div>
    );
};
